import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, PencilLine } from "lucide-react";
import { FormCard, FormCardButton } from "@/components/FormCard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { TabNavigationBodyProps, TabWithContent } from "./types";
import { useTranslation } from "react-i18next";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation"; // ✅ ONLY ADDITION

export function TabNavigationBody({
  routes,
  currentTabId,
  onTabChange,
  children,
  onSaveDraft,
  onSubmit,
  isSubmitDisabled = false,
  isLoading = false,
  showCancel = true,
  showSaveDraft = false,
  askSaveDraft = false,
  customTitle,
  customSubtitle,
}: TabNavigationBodyProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const navigate = useNavigate();
  const { navToRoute } = useProtectedNavigation();
  const { t } = useTranslation();

  const effectiveCurrentTabId = currentTabId || routes[0]?.id || "";

  const currentTabIndex = routes.findIndex(
    (route) => route.id === effectiveCurrentTabId,
  );
  const currentTab = routes[currentTabIndex];
  const isLastTab = currentTabIndex === routes.length - 1;
  const isFirstTab = currentTabIndex === 0;
  const previousTab = !isFirstTab ? routes[currentTabIndex - 1] : null;
  const nextTab = !isLastTab ? routes[currentTabIndex + 1] : null;

  const handleCancel = () => {
    setShowConfirmDialog(true);
  };

  const confirmDiscard = () => {
    setShowConfirmDialog(false);
    navigate(-1);
  };

  const handlePrevious = () => {
    if (previousTab?.route) {
      const changeTab = () => {
        if (previousTab.route) {
          navToRoute(previousTab.route, previousTab.params);
        }
      };
      onTabChange(previousTab.id, changeTab);
    }
  };

  const handleNext = () => {
    if (nextTab?.route) {
      const changeTab = () => {
        if (nextTab.route) {
          navToRoute(nextTab.route, nextTab.params);
        }
      };
      onTabChange(nextTab.id, changeTab);
    }
  };

  const getTabTitle = (route: TabWithContent) =>
    route.content?.title || route.handle?.name || route.id;
  const getTabSubtitle = (route: TabWithContent) =>
    route.content?.subtitle || "";

  const title = customTitle || (currentTab ? getTabTitle(currentTab) : "");
  const subtitle =
    customSubtitle || (currentTab ? getTabSubtitle(currentTab) : "");

  let nextButton: FormCardButton | undefined = undefined;
  if (isLastTab) {
    if (onSubmit) {
      nextButton = {
        label: t("global.submit"),
        onClick: onSubmit,
        variant: "submit",
        disabled: isSubmitDisabled || isLoading,
        iconRight: <Check className="h-4 w-4" />,
      };
    }
  } else {
    if (nextTab) {
      nextButton = {
        label: t("global.next"),
        variant: "submit",
        onClick: handleNext,
        disabled: isLoading,
        iconRight: <ArrowRight className="h-4 w-4" />,
      };
    }
  }

  return (
    <>
      <FormCard
        title={title}
        description={subtitle}
        previousButton={
          !isFirstTab
            ? {
                label: t("global.previous"),
                variant: "outline",
                onClick: handlePrevious,
                disabled: isLoading,
              }
            : undefined
        }
        cancelButton={
          showCancel
            ? {
                label: t("global.cancel"),
                onClick: handleCancel,
                disabled: isLoading,
                variant: "outlineDestructive",
              }
            : undefined
        }
        saveAsDraftButton={
          showSaveDraft && onSaveDraft
            ? {
                label: t("global.saveAsDraft"),
                variant: "outline",
                icon: <PencilLine className="h-4 w-4" />,
                onClick: () =>
                  askSaveDraft ? setShowDraftDialog(true) : onSaveDraft(),
              }
            : undefined
        }
        nextButton={nextButton}
      >
        <div>{children}</div>
      </FormCard>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("bulkMessaging.navigation.areYouSure")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("bulkMessaging.navigation.youWillLoseYourChanges")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("global.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscard}>
              {t("global.discardChanges")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDraftDialog} onOpenChange={setShowDraftDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("bulkMessaging.navigation.areYouSure")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("bulkMessaging.navigation.saveDraft")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("global.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onSaveDraft}>
              {t("global.saveAsDraft")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

TabNavigationBody.displayName = "TabNavigationBody";
